import React, { useEffect, useState } from 'react';
import { Plus, XCircle } from 'lucide-react';
import delegationsService from '../services/delegations';
import { useApps } from '../contexts/AppsContext';
import { useNotice } from '../components/common/Notices';
import { useConfirm } from '../components/common/ConfirmDialog';
import FeatureOffState, { useFeatureGate } from '../components/common/FeatureOffState';
import { Button } from '../components/ui/Button';
import PageHeader from '../components/common/PageHeader';
import SkeletonList from '../components/common/SkeletonList';
import Modal from '../components/common/Modal';
import DataTable, { RowAction, RowActions } from '../components/common/DataTable';
import StatusBadge from '../components/common/StatusBadge';
import useServerTable from '../hooks/useServerTable';
import useAppUsers, { ALL_APPS } from '../hooks/useAppUsers';
import { Field } from '../components/ui/Field';
import { Select, TextInput } from '../components/ui/Input';

const TABS = [
    { key: 'all', label: 'All Delegations' },
    { key: 'granted', label: 'Granted by Me' },
    { key: 'received', label: 'Granted to Me' },
];

const STATUS_OPTIONS = [
    { value: 'active', label: 'Active' },
    { value: 'revoked', label: 'Revoked' },
    { value: 'expired', label: 'Expired' },
];

const TYPE_OPTIONS = [
    { value: 'read_only', label: 'Read only' },
    { value: 'full', label: 'Full access' },
    { value: 'emergency_only', label: 'Emergency only' },
];

const EMPTY_FORM = {
    grantor_user_id: '',
    grantee_user_id: '',
    delegation_type: 'read_only',
    relationship: '',
    effective_until: '',
};

const DelegationsPage = () => {
    const { loading: loadingApps, selectedAppId: appId, selectedApp } = useApps();
    const notice = useNotice();
    const confirm = useConfirm();
    const gate = useFeatureGate(appId);
    const [tab, setTab] = useState('all');
    const [error, setError] = useState('');
    const [showGrantModal, setShowGrantModal] = useState(false);
    // Either party can be any active user; refetched each time the grant modal opens.
    const usersQuery = useAppUsers(ALL_APPS, { enabled: showGrantModal });
    const allUsers = usersQuery.items;
    const [formData, setFormData] = useState(EMPTY_FORM);

    // URL keys: status (not on "Granted to Me"), type, effective_until_*, created_*.
    // Each tab's endpoint only joins the other party's details; the caller's own side is "You".
    const personColumn = (role, header) => ({
        key: role,
        header,
        sortKey: (role === 'grantor' && tab === 'granted') || (role === 'grantee' && tab === 'received') ? null : role,
        render: (d) => {
            const isMe = (role === 'grantor' && tab === 'granted') || (role === 'grantee' && tab === 'received');
            if (isMe) return <span className="text-slate-500">You</span>;
            const name = [d[`${role}_first_name`], d[`${role}_last_name`]].filter(Boolean).join(' ');
            return (
                <div className="min-w-0">
                    <div className="font-medium text-slate-900">{name || '?'}</div>
                    {d[`${role}_email`] && <div className="text-xs text-slate-500 truncate">{d[`${role}_email`]}</div>}
                </div>
            );
        },
    });

    const columns = [
        personColumn('grantor', 'Grantor'),
        personColumn('grantee', 'Grantee'),
        {
            key: 'delegation_type',
            header: 'Type',
            sortKey: 'type',
            filter: { key: 'type', type: 'multiselect', options: TYPE_OPTIONS, defaultExpanded: true },
        },
        {
            key: 'relationship',
            header: 'Relationship',
            sortKey: 'relationship',
            render: (d) => d.relationship || '\u2014',
        },
        {
            key: 'effective_from',
            header: 'From',
            sortKey: 'from',
            render: (d) => d.effective_from?.slice(0, 10) || '\u2014',
        },
        {
            key: 'effective_until',
            header: 'Until',
            sortKey: 'until',
            render: (d) => d.effective_until?.slice(0, 10) || 'Until revoked',
            filter: { type: 'date' },
        },
        {
            key: 'status',
            header: 'Status',
            sortKey: 'status',
            render: (d) => <StatusBadge status={d.status} />,
            // "Granted to me" lists only active delegations, so it has no status filter.
            filter:
                tab === 'received'
                    ? undefined
                    : { type: 'multiselect', options: STATUS_OPTIONS, defaultExpanded: true },
        },
        {
            key: 'created_on',
            header: 'Granted',
            sortKey: 'created',
            defaultVisible: false,
            className: 'whitespace-nowrap text-slate-500',
            render: (d) => d.created_on?.slice(0, 10) || '\u2014',
            filter: { key: 'created', type: 'date' },
        },
        {
            key: 'actions',
            header: <span className="sr-only">Actions</span>,
            label: 'Row actions',
            locked: true,
            align: 'right',
            render: (d) =>
                d.status === 'active' ? (
                    <RowActions>
                        <RowAction
                            icon={XCircle}
                            label="Revoke delegation"
                            tone="danger"
                            onClick={() => handleRevoke(d)}
                        />
                    </RowActions>
                ) : null,
        },
    ];

    // One table across the three tabs: each tab is its own endpoint, all paged server-side.
    const table = useServerTable((query) => gate.guard(appId, delegationsService.getPage(appId, tab, query)), {
        deps: [appId, tab],
        enabled: Boolean(appId),
        defaultSort: { key: 'created', dir: 'desc' },
        columns,
    });

    // A grant in progress belongs to the previous app: close it on switch.
    useEffect(() => {
        setShowGrantModal(false);
        setFormData(EMPTY_FORM);
        setError('');
    }, [appId]);

    const handleTabChange = (newTab) => {
        // "Granted to Me" only lists active delegations, so a status filter would be meaningless there.
        if (newTab === 'received') table.setFilter('status', undefined);
        setTab(newTab);
    };

    const handleGrant = async () => {
        setError('');
        try {
            await delegationsService.grant({ ...formData, app_id: appId });
            setShowGrantModal(false);
            setFormData(EMPTY_FORM);
            table.reload();
            notice.success('Delegation granted.');
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to grant delegation.');
        }
    };

    const partyName = (d, role) => {
        const isMe = (role === 'grantor' && tab === 'granted') || (role === 'grantee' && tab === 'received');
        if (isMe) return 'you';
        return [d[`${role}_first_name`], d[`${role}_last_name`]].filter(Boolean).join(' ') || d[`${role}_email`] || '?';
    };

    const handleRevoke = async (d) => {
        const label = `from ${partyName(d, 'grantor')} to ${partyName(d, 'grantee')}`;
        const ok = await confirm({
            title: `Revoke the delegation ${label}?`,
            body: 'The delegate loses the delegated access immediately. To restore it, grant a new delegation.',
            confirmLabel: 'Revoke',
            tone: 'danger',
        });
        if (!ok) return;
        try {
            await delegationsService.revoke(d.delegation_id);
            table.reload();
            notice.success(`Delegation ${label} revoked.`);
        } catch (err) {
            notice.error(err, `Couldn’t revoke the delegation ${label}. Try again.`);
        }
    };

    const openGrantModal = () => {
        setError('');
        setShowGrantModal(true);
    };


    if (loadingApps) return <SkeletonList />;

    return (
        <div className="space-y-6">
            <PageHeader
                title="Delegations"
                actions={
                    appId &&
                    !gate.off && (
                        <Button onClick={openGrantModal}>
                            <Plus className="h-4 w-4" aria-hidden="true" /> Grant Delegation
                        </Button>
                    )
                }
            />

            {!appId ? (
                <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
                    <p className="text-slate-600">Choose an app in the sidebar to see its delegations.</p>
                </div>
            ) : gate.off ? (
                <FeatureOffState
                    feature="Delegated access"
                    appName={selectedApp?.app_name}
                    description="Delegated access lets one user act on another user’s behalf for a set period, such as cover during leave."
                />
            ) : (
                <>
                    {/* Tabs */}
                    <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
                        {TABS.map((t) => (
                            <button
                                key={t.key}
                                type="button"
                                aria-pressed={tab === t.key}
                                onClick={() => handleTabChange(t.key)}
                                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 ${
                                    tab === t.key
                                        ? 'bg-white shadow text-slate-900'
                                        : 'text-slate-600 hover:text-slate-900'
                                }`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>

                    <DataTable
                        {...table.tableProps}
                        columns={columns}
                        tableKey="delegations"
                        rowKey="delegation_id"
                        searchPlaceholder="Search name, email, type, relationship…"
                        emptyMessage="No delegations found."
                    />
                </>
            )}

            {/* Grant Modal */}
            <Modal isOpen={showGrantModal} onClose={() => setShowGrantModal(false)} title="Grant Delegation">
                <div className="space-y-4">
                    <Field label="Grantor (who grants access) *">
                        <Select
                            value={formData.grantor_user_id}
                            onChange={(e) => setFormData({ ...formData, grantor_user_id: e.target.value })}
                        >
                            <option value="">Select user...</option>
                            {allUsers.map((u) => (
                                <option key={u.user_id} value={u.user_id}>
                                    {u.first_name} {u.last_name} ({u.email_id})
                                </option>
                            ))}
                        </Select>
                    </Field>
                    <Field label="Grantee (who receives access) *">
                        <Select
                            value={formData.grantee_user_id}
                            onChange={(e) => setFormData({ ...formData, grantee_user_id: e.target.value })}
                        >
                            <option value="">Select user...</option>
                            {allUsers.map((u) => (
                                <option key={u.user_id} value={u.user_id}>
                                    {u.first_name} {u.last_name} ({u.email_id})
                                </option>
                            ))}
                        </Select>
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Access Type">
                            <Select
                                value={formData.delegation_type}
                                onChange={(e) => setFormData({ ...formData, delegation_type: e.target.value })}
                            >
                                <option value="read_only">Read only</option>
                                <option value="full">Full access</option>
                                <option value="emergency_only">Emergency only</option>
                            </Select>
                        </Field>
                        <Field label="Relationship">
                            <Select
                                value={formData.relationship}
                                onChange={(e) => setFormData({ ...formData, relationship: e.target.value })}
                            >
                                <option value="">Select...</option>
                                <option value="spouse">Spouse</option>
                                <option value="parent">Parent</option>
                                <option value="child">Child</option>
                                <option value="guardian">Guardian</option>
                                <option value="caregiver">Caregiver</option>
                                <option value="other">Other</option>
                            </Select>
                        </Field>
                    </div>
                    <Field label="Expires On (optional)">
                        <TextInput
                            type="date"
                            value={formData.effective_until}
                            onChange={(e) => setFormData({ ...formData, effective_until: e.target.value })}
                        />
                    </Field>
                    {(error || usersQuery.error) && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {error || usersQuery.error}
                        </div>
                    )}
                    <div className="flex justify-end gap-2 pt-2">
                        <button
                            onClick={() => setShowGrantModal(false)}
                            className="px-4 py-2 text-sm rounded-lg border border-slate-300 hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleGrant}
                            disabled={!formData.grantor_user_id || !formData.grantee_user_id}
                            className="px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                        >
                            Grant
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default DelegationsPage;
